import UserSettings from '../../utility/user-settings';
import GeneralUtility from '../../utility/general-utility';
import Pagination, {PaginationSettings} from '../../component/pagination';
import FiltersAction from './filters-action';
import ApiAction from './api-action';
import {ListResult} from '../../../types/list-result';
import {ApiResult} from '../../../types/api-result';
import TraceableError from '../../error/traceable-error';

/**
 * Base action for any list-endpoints of the Lawyer API.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default abstract class ListAction extends ApiAction {
  /**
   * Optional pagination component. Pagination is not initialized by default.
   *
   * @type Pagination
   * @private
   */
  protected _pagination: Pagination;


  /**
   * Gets the pagination component.
   *
   * @returns {Pagination}
   */
  get pagination(): Pagination {
    return this._pagination;
  }


  /**
   * Sets the pagination component.
   *
   * @param {Pagination} pagination
   * @returns {void}
   */
  set pagination(pagination: Pagination) {
    this._pagination = pagination;
  }


  /**
   * Initializes an optional pagination component.
   *
   * @returns {void}
   * @private
   */
  protected initializePagination() {
    // Setup pagination component
    const settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);
    const paginationSettings: PaginationSettings = {
      'start': settings.start,
      'rows': this.settings.rows,
      'numFound': 0,
      'maxLinks': 5
    };

    this.pagination = new Pagination(paginationSettings, this.container.querySelector('[data-container="pagination"]'));

    // Listen to when the pagination changes a page
    this.pagination.browseEvent.register(changedPaginationSettings => {
      // Persist new start settings
      const changedSettings = UserSettings.getInstance().getPluginSettings(this.plugin.name);
      changedSettings.start = changedPaginationSettings.start;
      UserSettings.getInstance().setPluginSettings(this.plugin.name, changedSettings);

      // Reload list
      this.update(changedSettings);

      // Scroll to the filters (if present), after a short timeout to wait for the result to render
      setTimeout(() => {
        this.scrollToFilters();
      }, 300);
    });
  }


  /**
   * Scrolls to a filter component, if one exists.
   *
   * @returns {void}
   * @protected
   */
  protected scrollToFilters() {
    this.plugin.getAction('filters', (action: FiltersAction) => {
      // Account for the fixed header
      const actionTopPosition = action.container.getBoundingClientRect().top + window.scrollY;
      const headerHeight = document.querySelector('#header').getBoundingClientRect().height;

      window.scrollTo({
        top: actionTopPosition - headerHeight,
        behavior: 'smooth'
      });
    });
  }


  /**
   * Initializes an optional filter component.
   *
   * @returns {void}
   * @private
   */
  protected initializeFilters() {
    // Wait for the filter action to be ready
    this.plugin.getAction('filters', (action: FiltersAction) => {
      action.changeEvent.register(changedSettings => {
        // Reset the pagination to page 1 and persist
        changedSettings.start = 0;
        UserSettings.getInstance().setPluginSettings(this.plugin.name, changedSettings);

        if (this.pagination) {
          this.pagination.reset();
        }

        // Fetch new result list when the filter changes
        this.update(changedSettings);
      });
    });
  }


  update(settings: any): void {
    // Show loading spinner
    const spinner = GeneralUtility.createSpinner({
      'overlay': {
        'enabled': true,
        'opacity': 0.25
      }
    });
    this.resultContainer.append(spinner);

    // Create parameters for the API request
    const parameters = this.createRequestParameters(settings);

    // Notify before-update listeners
    this.beforeUpdateEvent.dispatch(parameters);

    fetch(parameters.url, {
        'method': 'GET'
      }
    ).then(response => {
      return response.json();
    })
      .then(result => {
        this.initializeResult(result, settings);
      })
      .catch(error => {
        throw new TraceableError('Updating list failed.', error);
      });
  }


  protected initializeResult(result: ApiResult, settings: any) {
    const listResult = result as ListResult;

    // Update pagination
    if (this.pagination) {
      this.pagination.updateNumFound(listResult.numFound);
    }

    // Reset result container
    this.resultContainer.innerHTML = '';
    if (listResult.numFound === 0) {
      this.showNoResultContainer();
      return;
    }
    this.hideNoResultContainer();
  }
}
